import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import api from '../services/apiService';
import { UserContext } from '../contexts/UserContext';

const CooperatingCompanies = () => {
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Cooperating companies state
  const [cooperatingCompanies, setCooperatingCompanies] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState('');

  // Load data when component mounts
  useEffect(() => {
    if (user?.company_can_inspection) {
      loadCooperatingCompanies();
      loadAvailableCompanies();
    }
  }, [user]);

  const loadCooperatingCompanies = async () => {
    setLoading(true);
    try {
      const response = await api.get('/cooperating-companies/manage/');
      setCooperatingCompanies(response.data);
    } catch (error) {
      console.error('Error loading cooperating companies:', error);
      toast.error('Error loading cooperating companies');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableCompanies = async () => {
    try {
      const response = await api.get('/cooperating-companies/');
      setAvailableCompanies(response.data);
    } catch (error) {
      console.error('Error loading available companies:', error);
      toast.error('Error loading available companies');
    }
  };

  const openAddModal = () => {
    setSelectedCompany('');
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setSelectedCompany('');
  };

  const addCooperatingCompany = async () => {
    if (!selectedCompany) {
      toast.error('Please select a company');
      return;
    }

    setSaving(true);
    try {
      await api.post('/cooperating-companies/manage/', {
        company_id: selectedCompany
      });

      toast.success('Company added to cooperation list successfully');
      closeAddModal();
      loadCooperatingCompanies();
      loadAvailableCompanies(); // Refresh to remove added company from available list
    } catch (error) {
      console.error('Error adding cooperating company:', error);
      const errorMessage = error.response?.data?.error || 'Error adding company';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const removeCooperatingCompany = async (company) => {
    if (!window.confirm(`Are you sure you want to remove "${company.name}" from your cooperation list?`)) {
      return;
    }

    try {
      await api.delete('/cooperating-companies/manage/', {
        data: { company_id: company.id }
      });

      toast.success('Company removed from cooperation list successfully');
      loadCooperatingCompanies();
      loadAvailableCompanies(); // Refresh to add removed company back to available list
    } catch (error) {
      console.error('Error removing cooperating company:', error);
      const errorMessage = error.response?.data?.error || 'Error removing company';
      toast.error(errorMessage);
    }
  };

  // Filter available companies to exclude already added ones
  const getFilteredAvailableCompanies = () => {
    const cooperatingIds = cooperatingCompanies.map(company => company.id);
    return availableCompanies.filter(company => !cooperatingIds.includes(company.id));
  };

  // Check if user's company has can_inspection permission
  if (!user?.company_can_inspection) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg">
        <p className="text-gray-500 mb-4">This feature is only available for companies with inspection permissions</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center pb-4 border-b">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Cooperating Companies</h2>
          <p className="text-sm text-gray-600">Manage companies that you cooperate with for PPE inspections</p>
        </div>
        <button
          onClick={openAddModal}
          className="px-4 py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 transition-colors flex items-center space-x-2"
        >
          <span>+</span>
          <span>Add Company</span>
        </button>
      </div>

      {/* Cooperating Companies List */}
      <div className="space-y-4">
        {cooperatingCompanies.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <p className="text-gray-500 mb-4">No cooperating companies configured</p>
            <button
              onClick={openAddModal}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Add First Company
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {cooperatingCompanies.map((company) => (
              <div key={company.id} className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-3">
                  <h4 className="font-semibold text-lg text-gray-900">{company.name}</h4>
                  <button
                    onClick={() => removeCooperatingCompany(company)}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>

                <div className="space-y-2 text-sm">
                  {company.address && (
                    <div>
                      <span className="text-gray-500">Address:</span>
                      <p className="text-gray-900">{company.address}</p>
                    </div>
                  )}

                  {company.tax_id && (
                    <div>
                      <span className="text-gray-500">Tax ID:</span>
                      <p className="text-gray-900">{company.tax_id}</p>
                    </div>
                  )}

                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Company Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeAddModal} />

            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  onClick={closeAddModal}
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                    Add Cooperating Company
                  </h3>

                  <form onSubmit={(e) => { e.preventDefault(); addCooperatingCompany(); }} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select Company *
                      </label>
                      <select
                        value={selectedCompany}
                        onChange={(e) => setSelectedCompany(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">Choose a company...</option>
                        {getFilteredAvailableCompanies().map(company => (
                          <option key={company.id} value={company.id}>
                            {company.name}{company.address && ` - ${company.address}`}
                          </option>
                        ))}
                      </select>
                      {getFilteredAvailableCompanies().length === 0 && (
                        <p className="mt-1 text-sm text-gray-500">
                          No more companies available to add to cooperation list.
                        </p>
                      )}
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                      <button
                        type="button"
                        onClick={closeAddModal}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={saving || !selectedCompany || getFilteredAvailableCompanies().length === 0}
                      >
                        {saving ? 'Adding...' : 'Add Company'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CooperatingCompanies;